/**
 * Загрузка данных для экрана Likes: Likes you, Matches, Your likes
 */

import { supabase } from './supabase'
import { getPhotoPublicUrl } from './profilePhotos'

function resolvePhotos(photos) {
  if (!Array.isArray(photos) || photos.length === 0) return []
  return photos.map((p) => (p && (p.startsWith('http') ? p : getPhotoPublicUrl(p))) || null).filter(Boolean)
}

function addPhotoUrls(profile) {
  const photos = resolvePhotos(profile.photos || [])
  return { ...profile, photos }
}

function calculateAge(dateOfBirth) {
  if (!dateOfBirth) return null
  const today = new Date()
  const birth = new Date(dateOfBirth)
  let age = today.getFullYear() - birth.getFullYear()
  const m = today.getMonth() - birth.getMonth()
  if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--
  return age
}

/**
 * Кто меня лайкнул (to_user_id = я)
 * Сортировка по дате лайка (новые сверху)
 */
export async function loadLikesYou(userId) {
  try {
    const { data: rows, error } = await supabase
      .from('likes')
      .select('from_user_id, created_at')
      .eq('to_user_id', userId)
      .order('created_at', { ascending: false })

    if (error) {
      console.error('[loadLikesYou]', error)
      return []
    }
    if (!rows || rows.length === 0) return []

    const ids = [...new Set(rows.map((r) => r.from_user_id))]
    const { data: profiles, error: profError } = await supabase
      .from('profiles')
      .select('id, name, photos, date_of_birth, gender, interests, place_of_birth, time_of_birth')
      .in('id', ids)

    if (profError || !profiles?.length) return []

    const byId = Object.fromEntries(profiles.map((p) => [p.id, p]))
    const ordered = ids.map((id) => byId[id]).filter(Boolean)
    return ordered.map((p) => {
      const withPhotos = addPhotoUrls(p)
      return { ...withPhotos, age: calculateAge(p.date_of_birth) }
    })
  } catch (e) {
    console.error('[loadLikesYou]', e)
    return []
  }
}

/**
 * Матчи (оба в паре)
 * Сортировка по дате матча (новые сверху)
 */
export async function loadMatches(userId) {
  try {
    const { data: rows, error } = await supabase
      .from('matches')
      .select('id, user1_id, user2_id, created_at')
      .or(`user1_id.eq.${userId},user2_id.eq.${userId}`)
      .order('created_at', { ascending: false })

    if (error) {
      console.error('[loadMatches]', error)
      return []
    }
    if (!rows || rows.length === 0) return []

    const otherIds = rows.map((r) => (r.user1_id === userId ? r.user2_id : r.user1_id))
    const { data: profiles, error: profError } = await supabase
      .from('profiles')
      .select('id, name, photos, date_of_birth, gender, interests, place_of_birth, time_of_birth')
      .in('id', otherIds)

    if (profError || !profiles?.length) return []

    const byId = Object.fromEntries(profiles.map((p) => [p.id, p]))
    const ordered = otherIds.map((id) => byId[id]).filter(Boolean)
    return ordered.map((p) => {
      const withPhotos = addPhotoUrls(p)
      return { ...withPhotos, age: calculateAge(p.date_of_birth) }
    })
  } catch (e) {
    console.error('[loadMatches]', e)
    return []
  }
}

/**
 * Кого я лайкнул (from_user_id = я)
 * Сортировка по дате (новые сверху)
 */
export async function loadYourLikes(userId) {
  try {
    const { data: rows, error } = await supabase
      .from('likes')
      .select('to_user_id, created_at')
      .eq('from_user_id', userId)
      .order('created_at', { ascending: false })

    if (error) {
      console.error('[loadYourLikes]', error)
      return []
    }
    if (!rows || rows.length === 0) return []

    const ids = rows.map((r) => r.to_user_id)
    const { data: profiles, error: profError } = await supabase
      .from('profiles')
      .select('id, name, photos, date_of_birth, gender, interests, place_of_birth, time_of_birth')
      .in('id', ids)

    if (profError || !profiles?.length) return []

    const byId = Object.fromEntries(profiles.map((p) => [p.id, p]))
    const ordered = ids.map((id) => byId[id]).filter(Boolean)
    return ordered.map((p) => {
      const withPhotos = addPhotoUrls(p)
      return { ...withPhotos, age: calculateAge(p.date_of_birth) }
    })
  } catch (e) {
    console.error('[loadYourLikes]', e)
    return []
  }
}

/**
 * Есть ли новые лайки/матчи (созданные после last_seen)
 * @returns {{ hasNewLikes: boolean, hasNewMatches: boolean }}
 */
export async function getHasNewLikesAndMatches(userId) {
  try {
    const { data: profile, error: pErr } = await supabase
      .from('profiles')
      .select('last_seen_likes_at, last_seen_matches_at')
      .eq('id', userId)
      .single()
    if (pErr || !profile) return { hasNewLikes: false, hasNewMatches: false }
    const seenLikes = profile.last_seen_likes_at ? new Date(profile.last_seen_likes_at).getTime() : 0
    const seenMatches = profile.last_seen_matches_at ? new Date(profile.last_seen_matches_at).getTime() : 0

    const { data: likeRow } = await supabase
      .from('likes')
      .select('created_at')
      .eq('to_user_id', userId)
      .gt('created_at', new Date(seenLikes).toISOString())
      .limit(1)
      .maybeSingle()
    const hasNewLikes = !!likeRow

    const { data: matchRow } = await supabase
      .from('matches')
      .select('created_at')
      .or(`user1_id.eq.${userId},user2_id.eq.${userId}`)
      .gt('created_at', new Date(seenMatches).toISOString())
      .limit(1)
      .maybeSingle()
    const hasNewMatches = !!matchRow
    return { hasNewLikes, hasNewMatches }
  } catch (e) {
    console.error('[getHasNewLikesAndMatches]', e)
    return { hasNewLikes: false, hasNewMatches: false }
  }
}

/**
 * Отметить лайки и матчи как просмотренные (при открытии вкладки Likes)
 */
export async function markLikesSeen(userId) {
  try {
    const now = new Date().toISOString()
    await supabase
      .from('profiles')
      .update({ last_seen_likes_at: now, last_seen_matches_at: now })
      .eq('id', userId)
  } catch (e) {
    console.error('[markLikesSeen]', e)
  }
}
